import React, { useEffect, useState } from 'react';

import { Loader, Select, SelectProps, useMantineTheme } from '@mantine/core';
import { SelectItem } from '@mantine/core/lib/Select/types';
import { useDebouncedValue } from '@mantine/hooks';
import useAutoCompleteWorkoutMovement from 'hooks/workoutMovement/useAutoCompleteWorkoutMovement';
import { ScoreTypeEnum } from 'types/enums';

interface IWorkoutMovementSelectProps extends Omit<SelectProps, 'data' | 'onChange'> {
  value: string | null;
  movementName?: string;
  onChange: (
    movementId: string | null,
    movementName: string | null,
    defaultScoreType: ScoreTypeEnum | null,
  ) => void;
}

const WorkoutMovementSelect = ({
  value,
  movementName,
  onChange,
  ...selectProps
}: IWorkoutMovementSelectProps): JSX.Element => {
  const theme = useMantineTheme();
  const [options, setOptions] = useState<SelectItem[]>([]);
  const [defaultScoreTypeById, setDefaultScoreTypeById] = useState<Record<string, ScoreTypeEnum | null>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 250);
  const { data, isSuccess, isLoading } = useAutoCompleteWorkoutMovement({
    search: debouncedSearchTerm,
  });

  useEffect(() => {
    if (isSuccess && data) {
      const movements = data.data.results.map(movement => ({
        value: movement.workoutMovementId,
        label: movement.name,
      }));
      const defaults = data.data.results.reduce<Record<string, ScoreTypeEnum | null>>((acc, movement) => {
        acc[movement.workoutMovementId] = (movement.defaultScoreType as ScoreTypeEnum | '' | null) || null;
        return acc;
      }, {});
      if (value && movementName && !movements.some(m => m.value === value)) {
        movements.unshift({ value, label: movementName });
      }
      setOptions(movements);
      setDefaultScoreTypeById(prev => ({ ...prev, ...defaults }));
    }
  }, [data, isSuccess, value, movementName]);

  const handleChange = (movementId: string | null): void => {
    const option = options.find(opt => opt.value === movementId);
    const name = movementId ? option?.label ?? null : null;
    const defaultScoreType = movementId ? defaultScoreTypeById[movementId] ?? null : null;
    onChange(movementId, typeof name === 'string' ? name : null, defaultScoreType);
  };

  return (
    <Select
      {...selectProps}
      data={options}
      searchable
      onSearchChange={setSearchTerm}
      nothingFound={isLoading ? 'Movements are loading...' : 'No movements found'}
      maxDropdownHeight={200}
      rightSection={isLoading ? <Loader size={theme.fontSizes.md} /> : undefined}
      clearable
      value={value}
      onChange={handleChange}
      withinPortal
    />
  );
};

export default WorkoutMovementSelect;
